import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiCheckCircle, FiImage, FiShield, FiX } from "react-icons/fi";
import api, { apiError } from "../../lib/api";
import RenderToast from "../../appcomponents/RenderToast";
import ChangePasswordModal from "../../Modal/ChangePassword";
import { selectUser, setUser } from "../../store/authSlice";
import styles from "./ProfileSetting.module.css";

export default function ProfileSetting() {
  const currentUser = useSelector(selectUser);
  const dispatch = useDispatch();
  const [name, setName] = useState(currentUser?.name || "");
  const [email, setEmail] = useState(currentUser?.email || "");
  const [imagePreview, setImagePreview] = useState(currentUser?.image || "");
  const [imageError, setImageError] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [removeImageRequested, setRemoveImageRequested] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setName(currentUser?.name || "");
    setEmail(currentUser?.email || "");
    setImagePreview(currentUser?.image || "");
    setImageFile(null);
    setImageError(false);
    setRemoveImageRequested(false);
  }, [currentUser]);

  useEffect(() => {
    if (!imageFile) return;

    setUploading(true);
    const preview = URL.createObjectURL(imageFile);
    setImagePreview(preview);
    setRemoveImageRequested(false);
    RenderToast({ type: "success", message: "Image attached successfully." });

    const timer = window.setTimeout(() => setUploading(false), 350);
    return () => {
      URL.revokeObjectURL(preview);
      window.clearTimeout(timer);
    };
  }, [imageFile]);

  const openFilePicker = () => inputRef.current?.click();

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    setImageError(false);
    setRemoveImageRequested(true);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    RenderToast({ type: "success", message: "Image removed." });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      RenderToast({ type: "error", message: "Full name is required." });
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append("name", name);
      data.append("email", email);
      if (imageFile) data.append("image", imageFile);
      if (removeImageRequested && !imageFile) {
        data.append("remove_image", "1");
      }

      const response = await api.post("/api/profile.php", data);
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to update profile.");
      }

      dispatch(setUser(response.data.data));
      RenderToast({
        type: "success",
        message: "Profile updated successfully.",
      });
      setImageFile(null);
      setRemoveImageRequested(false);
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const displayUsername = currentUser?.username || "-";
  const imageExists = Boolean(imagePreview) && !imageError;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.profileSettingInfo}>
            <h1>Profile Setting</h1>
          </div>
          <div className={styles.changePasswordBtn}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setPasswordModal(true)}
            >
              <FiShield />
              Change Password
            </button>
          </div>
        </div>
      </header>

      <form className={styles.card} onSubmit={handleSubmit}>
        <section className={styles.imageSection}>
          <div
            className={`${styles.avatarWrap} ${imageExists ? styles.avatarHasImage : styles.avatarEmpty}`}
            onClick={openFilePicker}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openFilePicker();
              }
            }}
          >
            {uploading ? (
              <div className={styles.spinner} aria-label="Uploading image" />
            ) : imageExists ? (
              <img
                src={imagePreview}
                alt="Profile preview"
                onError={() => setImageError(true)}
              />
            ) : null}

            <div className={styles.avatarOverlay}>
              <span className={styles.overlayIcon}>
                <FiImage />
              </span>
              <span>Upload Img</span>
            </div>

            {imageExists ? (
              <button
                type="button"
                className={styles.removeImageBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage();
                }}
                aria-label="Remove image"
              >
                <FiX />
              </button>
            ) : null}
          </div>
        </section>

        <section className={styles.fieldSection}>
          <label className={styles.field}>
            <span>Full Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
            />
          </label>

          <label className={styles.field}>
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </label>

          <label className={styles.field}>
            <span>Username</span>
            <input value={displayUsername} readOnly />
          </label>
        </section>

        <div className={styles.actions}>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={saving || uploading}
          >
            <FiCheckCircle />
            {saving ? "Saving..." : "Update Profile"}
          </button>
        </div>
      </form>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => setImageFile(e.target.files?.[0] || null)}
        className={styles.hiddenInput}
      />

      <ChangePasswordModal
        open={passwordModal}
        onClose={() => setPasswordModal(false)}
        onSuccess={() => setPasswordModal(false)}
      />
    </div>
  );
}
